import { Search, FileCheck, Building2, Globe, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Elegant library background
const PRACTICE_BG = "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=2000&q=80";

const practiceAreas = [
  {
    id: "due-diligence",
    icon: Search,
    title: "Property Due Diligence",
    description: "Comprehensive investigation of property history, ownership chains, and potential legal risks to ensure secure acquisition.",
    link: "#booking"
  },
  {
    id: "title-verification",
    icon: FileCheck,
    title: "Title Verification",
    description: "Verification of property title and ownership through land registries and regulatory agencies to confirm legitimacy and prevent disputes.",
    link: "#booking"
  },
  {
    id: "acquisition-advisory",
    icon: Building2,
    title: "Acquisition Advisory",
    description: "Strategic legal guidance throughout property acquisition — from negotiation and documentation to title perfection.",
    link: "#booking"
  },
  {
    id: "diaspora-services",
    icon: Globe,
    title: "Diaspora Services",
    description: "End-to-end legal support for Nigerians abroad acquiring property in Nigeria, ensuring secure transactions without the risks of remote purchasing.",
    link: "/diaspora"
  }
];

export const PracticeAreasGrid = () => {
  return (
    <section 
      id="services" 
      className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
      data-testid="practice-areas-grid"
    >
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${PRACTICE_BG})` }}
      />
      <div className="absolute inset-0 bg-[#0A0A0B]/95" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 lg:mb-20">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            Our Expertise
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mt-4 md:mt-6 mb-4 md:mb-6">
            Practice Areas
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mb-4 md:mb-6" />
          <p className="text-white/50 text-sm md:text-base lg:text-lg px-2">
            Specialized legal services to protect your property investments 
            and ensure secure transactions in Nigeria.
          </p>
        </div>

        {/* 4-Block Grid */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          {practiceAreas.map((area, index) => (
            <div
              key={area.id}
              className="group relative p-6 md:p-8 lg:p-10 bg-[#0A0A0B]/50 border border-white/10 hover:border-[#D4AF37]/50 backdrop-blur-sm transition-all duration-500"
              data-testid={`practice-area-${area.id}`}
            >
              {/* Number indicator - hidden on mobile */}
              <span className="absolute top-4 right-4 md:top-6 md:right-6 font-['Playfair_Display'] text-4xl md:text-6xl text-white/5 group-hover:text-[#D4AF37]/10 transition-colors hidden sm:block">
                0{index + 1}
              </span>
              
              <div className="relative z-10">
                <div className="w-12 h-12 md:w-16 md:h-16 border border-[#D4AF37]/30 flex items-center justify-center mb-5 md:mb-8 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all">
                  <area.icon className="w-5 h-5 md:w-8 md:h-8 text-[#D4AF37]" />
                </div>
                
                <h3 className="font-['Playfair_Display'] text-xl md:text-2xl lg:text-3xl text-white mb-3 md:mb-4">
                  {area.title}
                </h3>
                
                <p className="text-white/50 text-sm md:text-base leading-[1.7] md:leading-relaxed mb-5 md:mb-6">
                  {area.description}
                </p>
                
                {area.link.startsWith("/") ? (
                  <Link
                    to={area.link}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wide uppercase group-hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <a
                    href={area.link}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wide uppercase group-hover:gap-3 transition-all"
                  >
                    Enquire Now
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
